import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App.jsx";
import "./index.css";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import monitoringIntegration from "./lib/monitoring-integration.js";
import cspUtils from "./lib/csp-utils.js";
import { initPerformanceMonitoring } from "./lib/performance-utils.js";
import { preloadCriticalRoutes } from "./lib/lazyRoutes.jsx";

// Initialize comprehensive monitoring system BEFORE rendering the app
try {
  monitoringIntegration.trackUserAction('application_initialization_start');
} catch (error) {
  // Silent fail - monitoring is non-critical
}

// Initialize CSP utilities
try {
  if (cspUtils && typeof cspUtils.initialize === 'function') {
    cspUtils.initialize();
    const diagnostics = cspUtils.getDiagnostics();
    monitoringIntegration.trackUserAction('csp_utilities_initialized', diagnostics);
  }
} catch (error) {
  monitoringIntegration.trackError(error, { context: 'csp_initialization' });
}

// Stack Auth configuration (Clerk has been deprecated)

// Supabase configuration is handled in the SupabaseAuthProvider

// PostHog configuration
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || "";
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
const posthogOptions = {
  api_host: posthogHost,
  autocapture: true,
  capture_pageview: true,
  capture_pageleave: true,
  session_recording: {
    recordCrossOriginIframes: false,
  },
  persistence: "localStorage+cookie",
};

// Safe WebSocket token initialization with error handling
try {
  if (typeof window !== "undefined" && !window.__WS_TOKEN__) {
    window.__WS_TOKEN__ = `token-${Date.now()}`;
    globalThis.__WS_TOKEN__ = window.__WS_TOKEN__;
  }
} catch {
  // Fallback token
  if (typeof window !== "undefined") {
    window.__WS_TOKEN__ = `fallback-${Date.now()}`;
  }
}

// Initialization safety check
const initializationCheck = () => {
  if (typeof window === "undefined") {
    throw new Error("Window object not available");
  }
  if (!document.getElementById("root")) {
    throw new Error("Root element not found");
  }
  return true;
};

// Safe QueryClient initialization
let queryClient;
try {
  monitoringIntegration.trackUserAction('query_client_initialization_start');
  
  queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          monitoringIntegration.trackAPICall('retry', 'query_retry', {
            failureCount,
            error: error.message
          });
          return failureCount < 2;
        },
        onSuccess: (data, query) => {
          monitoringIntegration.trackAPICall('success', query.queryKey[0], {
            dataSize: JSON.stringify(data).length
          });
        },
        onError: (error, query) => {
          monitoringIntegration.trackAPICall('error', query.queryKey[0], {
            error: error.message
          });
        }
      },
    },
    errorHandler: (error) => {
      monitoringIntegration.trackError(error, { context: 'query_client_error_handler' });
    },
  });
  
  monitoringIntegration.trackUserAction('query_client_initialization_success');
} catch (error) {
  monitoringIntegration.trackError(error, { context: 'query_client_initialization' });

  // Fallback minimal QueryClient
  queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  monitoringIntegration.trackUserAction('query_client_fallback_used');
}

// Safe application initialization with comprehensive error handling
const initializeApp = () => {
  const initStartTime = performance.now();
  window.__appInitStarted = true;
  
  try {
    // Track initialization start
    monitoringIntegration.trackUserAction('app_initialization_checks_start');
    
    // Run initialization checks
    initializationCheck();
    window.__appInitChecksPassed = true;
    monitoringIntegration.trackUserAction('app_initialization_checks_passed');

    const rootElement = document.getElementById("root");
    if (!rootElement) {
      throw new Error("Root element not found");
    }
    window.__appRootFound = true;
    monitoringIntegration.trackUserAction('app_root_element_found');

    const root = createRoot(rootElement);
    window.__appRootCreated = true;
    monitoringIntegration.trackUserAction('app_root_created');

    // Wrap App with Sentry profiler for performance monitoring
    const SentryApp = Sentry.withProfiler(App);

    root.render(
      <StrictMode>
        <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <SentryApp />
            </QueryClientProvider>
          </ErrorBoundary>
        </PostHogProvider>
      </StrictMode>,
    );
    window.__appRendered = true;

    const initDuration = performance.now() - initStartTime;
    monitoringIntegration.trackPerformance('app_initialization', initDuration);
    monitoringIntegration.trackUserAction('application_initialized_successfully', {
      duration: Math.round(initDuration)
    });
    monitoringIntegration.trackBusinessEvent('application_launched', {
      version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      environment: import.meta.env.MODE || 'development',
      initDuration: Math.round(initDuration)
    });

    // Initialize performance monitoring after successful render
    if (typeof window !== "undefined") {
      try {
        initPerformanceMonitoring();
      } catch {
        // Non-critical - monitoring can fail silently
      }

      // Preload critical routes on idle
      try {
        preloadCriticalRoutes();
      } catch {
        // Non-critical - preloading can fail silently
      }
    }
  } catch (error) {
    window.__appError = error.message;
    window.__appErrorStack = error.stack;
    
    // Track initialization error
    monitoringIntegration.trackError(error, {
      context: 'app_initialization',
      step: 'rendering',
      window__appInitStarted: window.__appInitStarted,
      window__appInitChecksPassed: window.__appInitChecksPassed,
      window__appRootFound: window.__appRootFound,
      window__appRootCreated: window.__appRootCreated
    });
    
    monitoringIntegration.trackUserAction('application_initialization_failed', {
      error: error.message,
      stack: error.stack
    });

    // Fallback rendering
    try {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
            <h1>Wixell - Loading Issue</h1>
            <p>Error: ${error.message}</p>
            <p>Please refresh the page or clear your browser cache.</p>
            <button onclick="window.location.reload()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Refresh Page
            </button>
          </div>
        `;
        monitoringIntegration.trackUserAction('fallback_rendering_successful');
      }
    } catch (fallbackError) {
      window.__fallbackError = fallbackError.message;
      monitoringIntegration.trackError(fallbackError, {
        context: 'fallback_rendering',
        originalError: error.message
      });
    }
  }
};

// Initialize with delay to ensure all polyfills are ready
if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeApp);
  } else {
    // Small delay to ensure polyfills are loaded
    setTimeout(initializeApp, 100);
  }
} else {
  initializeApp();
}
